import React from 'react';
import { motion } from 'framer-motion';
import { cn } from 'lib/utils';

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  /** Accessible name when the switch has no visible `label`. */
  ariaLabel?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Sizes the clickable row that holds the track and its label. */
  className?: string;
}

const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  ariaLabel,
  size = 'sm',
  className,
}) => {
  const sizes = {
    sm: { track: 'w-8 h-5', thumb: 'top-[3px] w-3.5 h-3.5', translate: checked ? 16 : 2 },
    md: { track: 'w-11 h-6', thumb: 'top-1 w-4 h-4', translate: checked ? 24 : 4 },
    lg: { track: 'w-14 h-8', thumb: 'top-1 w-6 h-6', translate: checked ? 28 : 4 },
  };

  const currentSize = sizes[size];

  return (
    <label
      className={cn(
        'flex items-center gap-3 cursor-pointer',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          'relative rounded-full transition-colors duration-200',
          currentSize.track,
          checked ? 'bg-primary shadow-[inset_0_0_0_1px_rgba(17,24,39,0.06)]' : 'bg-muted shadow-[inset_0_0_0_1px_hsl(var(--border))]'
        )}
      >
        <motion.div
          className={cn('absolute left-0 rounded-full bg-white shadow-[0_1px_2px_rgba(17,24,39,0.25)]', currentSize.thumb)}
          animate={{ x: currentSize.translate }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      </button>
      {(label || description) && (
        <div className="flex-1">
          {label && <span className="text-sm font-medium text-foreground">{label}</span>}
          {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
        </div>
      )}
    </label>
  );
};

export { Switch };
export default Switch;
